package dev.lingvo.shared.screenshots.doc

import dev.lingvo.shared.StoreLimits

/**
 * One iterative pass over raw JSON text before any parser sees it (SPEC-B 1.3 step 1).
 *
 * kotlinx.serialization's tree reader recurses once per nesting level, so `[[[[...` ten thousand deep overflows the
 * stack on the JVM and in the browser. This scan has no recursion and no allocation per token: it tracks string
 * state and escapes, counts depth, values and string lengths, and stops at the first limit crossed, so a hostile
 * body costs at most one linear pass over at most [DesignLimits.MAX_RAW_BYTES] characters.
 *
 * It does not validate JSON syntax; the parser does that once the input is known to be small and shallow.
 */
object JsonPrecheck {
    /** The first limit [raw] crosses, or null when the parser may safely run. */
    fun scan(raw: String): DesignIssue? {
        // Every UTF-16 unit is at least one UTF-8 byte, so a long string is refused without measuring it.
        if (raw.length > DesignLimits.MAX_RAW_BYTES) return tooLarge(null)
        val bytes = StoreLimits.utf8Length(raw)
        if (bytes > DesignLimits.MAX_RAW_BYTES) return tooLarge(bytes)
        return scanStructure(raw)
    }

    private fun scanStructure(raw: String): DesignIssue? {
        var depth = 0
        var values = 0
        var inString = false
        var stringChars = 0
        var stringStart = 0
        var pendingString = false
        var inLiteral = false
        var lastWasHigh = false
        var index = 0
        val length = raw.length
        while (index < length) {
            val char = raw[index]
            if (inString) {
                when {
                    char == '\\' -> {
                        // An escape is one character of the decoded string: `\n` and `\u00e9` alike, and an escaped
                        // surrogate pair is one code point, as it is unescaped.
                        val unicode = index + 1 < length && raw[index + 1] == 'u'
                        val unit = if (unicode) hexUnit(raw, index + 2) else -1
                        val trailing = unit in 0xDC00..0xDFFF && lastWasHigh
                        lastWasHigh = unit in 0xD800..0xDBFF
                        index += if (unicode) 6 else 2
                        if (!trailing) stringChars++
                        if (stringChars > DesignLimits.MAX_STRING_CHARS) return stringTooLong(stringStart)
                        continue
                    }
                    char == '"' -> {
                        inString = false
                        pendingString = true
                    }
                    else -> {
                        // Code points, not UTF-16 units: the trailing half of a surrogate pair is not counted.
                        val trailing = char.isLowSurrogate() && lastWasHigh
                        lastWasHigh = char.isHighSurrogate()
                        if (!trailing) stringChars++
                        if (stringChars > DesignLimits.MAX_STRING_CHARS) return stringTooLong(stringStart)
                    }
                }
                index++
                continue
            }
            if (pendingString && !isWhitespace(char)) {
                // A string followed by ':' was a key; anything else makes it a value.
                if (char != ':') values++
                pendingString = false
            }
            when {
                char == '"' -> {
                    inLiteral = false
                    inString = true
                    stringChars = 0
                    stringStart = index
                    lastWasHigh = false
                }
                char == '{' || char == '[' -> {
                    inLiteral = false
                    depth++
                    values++
                    if (depth > DesignLimits.MAX_DEPTH) return tooDeep(index)
                }
                char == '}' || char == ']' -> {
                    inLiteral = false
                    if (depth > 0) depth--
                }
                char == ',' || char == ':' || isWhitespace(char) -> inLiteral = false
                !inLiteral -> {
                    // The first character of a number, true, false, null (or garbage the parser will refuse).
                    inLiteral = true
                    values++
                }
            }
            if (values > DesignLimits.MAX_VALUES) return tooManyValues()
            index++
        }
        if (pendingString) values++
        if (values > DesignLimits.MAX_VALUES) return tooManyValues()
        return null
    }

    /** The UTF-16 unit of four hex digits at [at], or -1 when they are not four hex digits. */
    private fun hexUnit(raw: String, at: Int): Int {
        if (at + 4 > raw.length) return -1
        var value = 0
        for (offset in 0 until 4) {
            val digit = raw[at + offset].digitToIntOrNull(16) ?: return -1
            value = value * 16 + digit
        }
        return value
    }

    private fun isWhitespace(char: Char): Boolean = char == ' ' || char == '\n' || char == '\r' || char == '\t'

    private fun tooLarge(bytes: Int?): DesignIssue = DesignIssue(
        DesignIssueCode.PAYLOAD_TOO_LARGE,
        "",
        "The design is ${bytes?.let { "$it bytes" } ?: "too large"}; the limit is ${DesignLimits.MAX_RAW_BYTES} bytes.",
    )

    private fun tooDeep(offset: Int): DesignIssue = DesignIssue(
        DesignIssueCode.TOO_DEEP,
        "",
        "The design nests deeper than ${DesignLimits.MAX_DEPTH} levels (at character $offset).",
    )

    private fun tooManyValues(): DesignIssue = DesignIssue(
        DesignIssueCode.TOO_MANY_TOKENS,
        "",
        "The design holds more than ${DesignLimits.MAX_VALUES} values.",
    )

    private fun stringTooLong(offset: Int): DesignIssue = DesignIssue(
        DesignIssueCode.STRING_TOO_LONG,
        "",
        "A string starting at character $offset is longer than ${DesignLimits.MAX_STRING_CHARS} characters.",
    )
}
